import { Box, Button, Grid, Modal, IconButton, Snackbar, Alert, Chip, Autocomplete, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import React from 'react';
import { useSelector } from 'react-redux';
import { LineupApiResponse } from '../../preContest/queries/queryTypes';
import RoiBoostButton from './RoiBoostButton';
import { useLocalStorage } from 'usehooks-ts';
import { GridApi, GridValidRowModel } from '@mui/x-data-grid';
import { findLineupsToFavorite, getLineupPlayerKeys } from './utils';
import { SlateResponse } from '../store/types';
import { IRootState } from '../../../store/reducer';

interface PlayerGroup {
  id: string;
  players: LineupApiResponse[];
  roiBoost: number;
}

interface GroupRowProps {
  group: PlayerGroup;
  availablePlayers: LineupApiResponse[];
  exposure: string;
  onPlayersChange: (groupId: string, players: LineupApiResponse[]) => void;
  onBoostChange: (groupId: string, boost: number) => void;
  onDelete: (groupId: string) => void;
}

const GroupRow = React.memo(({ group, availablePlayers, exposure, onPlayersChange, onBoostChange, onDelete }: GroupRowProps) => {
  return (
    <Box
      sx={{
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        borderRadius: 1,
        padding: 2,
        marginBottom: 2,
        border: '1px solid rgba(255, 255, 255, 0.1)',
      }}
    >
      <Grid container alignItems="center" spacing={2}>
        <Grid item xs={5}>
          <Box sx={{ fontSize: 11, fontWeight: 700, color: 'rgba(255, 255, 255, 0.7)', textTransform: 'uppercase', marginBottom: 0.5 }}>
            Players
          </Box>
          <Autocomplete
            multiple
            options={availablePlayers}
            value={group.players}
            getOptionLabel={(option) => {
              const posPrefix = ['CPT', 'FLEX'].includes(option.position?.toUpperCase())
                ? `${option.position} - `
                : '';
              return `${posPrefix}${option.player}`;
            }}
            isOptionEqualToValue={(option, value) => {
              // For Showdown, same player can be CPT or FLEX - treat them as separate
              const optionPos = option.position?.toUpperCase();
              const valuePos = value.position?.toUpperCase();
              if (['CPT', 'FLEX'].includes(optionPos) || ['CPT', 'FLEX'].includes(valuePos)) {
                return option.exportableNameAndId === value.exportableNameAndId && optionPos === valuePos;
              }
              return option.exportableNameAndId === value.exportableNameAndId;
            }}
            filterOptions={(options, { inputValue }) => {
              if (!inputValue) return options.slice(0, 10);
              return options.filter(p =>
                p.player?.toLowerCase().includes(inputValue.toLowerCase())
              ).slice(0, 10);
            }}
            onChange={(_, newValue) => onPlayersChange(group.id, newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={group.players.length === 0 ? "Search players..." : ""}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    flexWrap: 'wrap',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                    '&.Mui-focused fieldset': { borderColor: 'rgba(1, 90, 206, 0.70)' },
                  },
                  '& .MuiInputBase-input': { color: 'white', fontSize: 13 },
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((player, index) => {
                const pos = player.position?.toUpperCase();
                const uniqueKey = ['CPT', 'FLEX'].includes(pos)
                  ? `${player.exportableNameAndId}||${pos}`
                  : player.exportableNameAndId;
                return (
                <Chip
                  {...getTagProps({ index })}
                  key={uniqueKey}
                  label={['CPT', 'FLEX'].includes(player.position?.toUpperCase())
                    ? `${player.position} - ${player.player}`
                    : player.player}
                  size="small"
                  variant="outlined"
                  sx={{
                    backgroundColor: 'transparent',
                    color: 'white',
                    fontWeight: 500,
                    fontSize: 13,
                    height: 28,
                    borderRadius: '14px',
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    '& .MuiChip-deleteIcon': {
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: 18,
                      '&:hover': { color: 'white' },
                    },
                  }}
                />
              );
              })
            }
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ fontSize: 13 }}>
                {['CPT', 'FLEX'].includes(option.position?.toUpperCase())
                  ? `${option.position} - ${option.player}`
                  : option.player}
              </Box>
            )}
            sx={{ width: '100%' }}
          />
        </Grid>
        <Grid item xs={2}>
          <Box sx={{ fontSize: 11, fontWeight: 700, color: 'rgba(255, 255, 255, 0.7)', textTransform: 'uppercase', marginBottom: 0.5 }}>
            Group Exposure
          </Box>
          <Box sx={{ fontSize: 16, fontWeight: 700, color: 'white' }}>
            {exposure}
          </Box>
        </Grid>
        <Grid item xs={3}>
          <Box sx={{ fontSize: 11, fontWeight: 700, color: 'rgba(255, 255, 255, 0.7)', textTransform: 'uppercase', marginBottom: 0.5 }}>
            ROI Boost
          </Box>
          <RoiBoostButton
            defaultPercent={0}
            onChange={(percent) => onBoostChange(group.id, percent)}
            previousPercent={group.roiBoost}
          />
        </Grid>
        <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton
            onClick={() => onDelete(group.id)}
            sx={{ color: 'rgba(255, 255, 255, 0.5)', '&:hover': { color: '#ff4444' } }}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </Grid>
      </Grid>
    </Box>
  );
});

interface OwnProps {
  readonly open: boolean;
  readonly players: LineupApiResponse[];
  readonly lineups?: LineupApiResponse[];
  readonly closeModal: () => void;
  readonly selectedSlate: SlateResponse;
  readonly playerGroups: PlayerGroup[];
  readonly setPlayerGroups: (groups: PlayerGroup[]) => void;
  readonly playerExposureDict: { [key: string]: number };
  readonly stackExposureDict: { [key: string]: number };
  readonly playerRoiList?: { [key: string]: number };
  readonly stackRoiList?: { [key: string]: number };
  readonly onAdjustFavorites: () => void;
  readonly apiRef: React.MutableRefObject<GridApi>;
}

const PlayerGroupsModal = (props: OwnProps) => {
  const {
    open,
    players,
    lineups,
    closeModal,
    selectedSlate,
    playerGroups,
    setPlayerGroups,
    playerExposureDict,
    stackExposureDict,
    playerRoiList = {},
    stackRoiList = {},
    onAdjustFavorites,
    apiRef,
  } = props;

  // Get unique limit from Redux state (set by QuickFavorite)
  const { uniqueLimit } = useSelector((state: IRootState) => state.common);

  const [snackbarOpen, setSnackbarOpen] = React.useState(false);

  // Local state for editing - only committed when "Adjust Favorites" is clicked
  const [localGroups, setLocalGroupsBase] = React.useState<PlayerGroup[]>(playerGroups);

  // Keep refs to the latest values for use in callbacks to avoid stale closures
  const localGroupsRef = React.useRef(localGroups);
  const playerRoiListRef = React.useRef(playerRoiList);
  const stackRoiListRef = React.useRef(stackRoiList);

  // Update refs synchronously during render
  localGroupsRef.current = localGroups;
  playerRoiListRef.current = playerRoiList;
  stackRoiListRef.current = stackRoiList;

  // Wrapper for setLocalGroups that also updates the ref immediately
  const setLocalGroups = React.useCallback((updater: PlayerGroup[] | ((prev: PlayerGroup[]) => PlayerGroup[])) => {
    setLocalGroupsBase(prev => {
      const newValue = typeof updater === 'function' ? updater(prev) : updater;
      localGroupsRef.current = newValue; // Update ref immediately
      return newValue;
    });
  }, []);

  // Sync local state only when modal opens (not on every playerGroups change to avoid re-render cascade)
  const prevOpenRef = React.useRef(open);
  React.useEffect(() => {
    if (open && !prevOpenRef.current) {
      // Modal just opened - sync with parent state
      setLocalGroups(playerGroups);
    }
    prevOpenRef.current = open;
  }, [open, playerGroups]);

  const [savedLineups, setSavedLineups] = useLocalStorage<(LineupApiResponse | GridValidRowModel)[]>(
    `precontest-lineups-${selectedSlate.slateId}`,
    []
  );

  // Sort players by exposure (highest first) for better default ordering in autocomplete
  const sortedPlayers = React.useMemo(() => {
    return [...players].sort((a, b) => (b.exposure || 0) - (a.exposure || 0));
  }, [players]);

  const createNewGroup = () => {
    const newGroup: PlayerGroup = {
      id: `group-${Date.now()}`,
      players: [],
      roiBoost: 0,
    };
    setLocalGroups(prev => [...prev, newGroup]);
  };

  const deleteGroup = React.useCallback((groupId: string) => {
    setLocalGroups(prev => prev.filter(g => g.id !== groupId));
  }, []);

  const updateGroupPlayers = React.useCallback((groupId: string, newPlayers: LineupApiResponse[]) => {
    setLocalGroups(prev => prev.map(g => {
      if (g.id === groupId) {
        return { ...g, players: newPlayers };
      }
      return g;
    }));
  }, []);

  const updateGroupBoost = React.useCallback((groupId: string, boost: number) => {
    setLocalGroups(prev => prev.map(group => {
      if (group.id === groupId) {
        return { ...group, roiBoost: boost };
      }
      return group;
    }));
  }, []);

  // Calculate group exposure - percentage of saved lineups that contain ALL players in the group
  // For Showdown, CPT is always the first player in exportableLineup, rest are FLEX
  const getGroupExposure = (group: PlayerGroup): string => {
    if (group.players.length === 0 || savedLineups.length === 0) return '0%';

    const lineupsWithAllPlayers = savedLineups.filter(lineup => {
      const lineupPlayerIds = lineup.exportableLineup.split(',').map(p => p.trim());
      const cptPlayerId = lineupPlayerIds[0]; // First player is CPT
      const flexPlayerIds = lineupPlayerIds.slice(1); // Rest are FLEX

      return group.players.every(player => {
        const playerPos = player.position?.toUpperCase();
        if (playerPos === 'CPT') {
          return player.exportableNameAndId === cptPlayerId;
        } else if (playerPos === 'FLEX') {
          return flexPlayerIds.includes(player.exportableNameAndId);
        }
        // Non-showdown: just check if player is in lineup
        return lineupPlayerIds.includes(player.exportableNameAndId);
      });
    });

    const exposure = (lineupsWithAllPlayers.length / savedLineups.length) * 100;
    return `${exposure.toFixed(1)}%`;
  };

  // Get players available for a specific group (not already in that group)
  // For Showdown, CPT and FLEX are treated as separate players
  // Uses sortedPlayers (sorted by exposure) for better default ordering
  const getAvailablePlayersForGroup = (group: PlayerGroup) => {
    const groupPlayerKeys = group.players.map(p => {
      const pos = p.position?.toUpperCase();
      return ['CPT', 'FLEX'].includes(pos)
        ? `${p.exportableNameAndId}||${pos}`
        : p.exportableNameAndId;
    });
    return sortedPlayers.filter(p => {
      const pos = p.position?.toUpperCase();
      const playerKey = ['CPT', 'FLEX'].includes(pos)
        ? `${p.exportableNameAndId}||${pos}`
        : p.exportableNameAndId;
      return !groupPlayerKeys.includes(playerKey);
    });
  };


  // Calculate group boost for a lineup based on which groups have all their players in the lineup
  // Uses exportableLineup (not fullLineup) to match the format used in newTableConfig.tsx
  const getLineupGroupBoost = (lineup: LineupApiResponse, groups: PlayerGroup[]): number => {
    if (!lineup?.exportableLineup || groups.length === 0) return 0;

    const lineupPlayerIds = lineup.exportableLineup.split(',').map(p => p.trim());
    const cptPlayerId = lineupPlayerIds[0]; // First player is CPT (for Showdown)
    const flexPlayerIds = lineupPlayerIds.slice(1); // Rest are FLEX
    let totalBoost = 0;

    groups.forEach(group => {
      if (group.players.length === 0 || group.roiBoost === 0) return;

      const allPlayersInLineup = group.players.every(player => {
        const playerPos = player.position?.toUpperCase();
        if (playerPos === 'CPT') {
          return player.exportableNameAndId === cptPlayerId;
        } else if (playerPos === 'FLEX') {
          return flexPlayerIds.includes(player.exportableNameAndId);
        }
        // Non-showdown: just check if player is in lineup
        return lineupPlayerIds.includes(player.exportableNameAndId);
      });

      if (allPlayersInLineup) {
        totalBoost += group.roiBoost;
      }
    });

    return totalBoost / 100; // Convert percentage to decimal
  };

  // Refavorite lineups using the lineups array directly (for when grid doesn't have lineup data)
  const refavoriteFromLineupsArray = (lineupCount: number) => {
    if (!lineups || lineups.length === 0) return;

    // Apply group boosts and sort by effective simLineupRoi
    const lineupsWithBoosts = lineups
      .filter(l => l.exportableLineup && l.exportableLineup.length > 0) // Only actual lineups
      .map(lineup => {
        const groupBoost = getLineupGroupBoost(lineup, localGroups);
        const effectiveRoi = (lineup.simLineupRoi || 0) + groupBoost;
        return { ...lineup, effectiveRoi };
      })
      .sort((a, b) => b.effectiveRoi - a.effectiveRoi);

    // Use canFavoriteLineup logic to select lineups respecting exposure limits
    let currentPlayerExposure: { [key: string]: number } = {};
    let currentStackExposure: { [key: string]: number } = {};
    const globalPlayerExposure = playerExposureDict['global'] || 100;
    const globalStackExposure = stackExposureDict['global'] || 100;
    let favoritedLineups: LineupApiResponse[] = [];

    for (const lineup of lineupsWithBoosts) {
      if (favoritedLineups.length >= lineupCount) break;

      // Check player exposures
      const lineupKeys = getLineupPlayerKeys(lineup.exportableLineup || '', selectedSlate);
      let canAdd = true;

      for (const key of lineupKeys) {
        const currentExp = currentPlayerExposure[key] || 0;
        const maxExp = Math.min(playerExposureDict[key] ?? 100, globalPlayerExposure) * lineupCount / 100;
        if (currentExp + 1 > maxExp) {
          canAdd = false;
          break;
        }
      }

      // Check stack exposures
      if (canAdd && lineup.stacks) {
        for (const stack of lineup.stacks) {
          if ((selectedSlate?.sport?.toLowerCase() === 'mlb' && stack.count > 2) ||
              (selectedSlate?.sport?.toLowerCase() === 'nfl' && stack.count > 0)) {
            const currentExp = currentStackExposure[stack.team] || 0;
            const maxExp = Math.min(stackExposureDict[stack.team] ?? 100, globalStackExposure) * lineupCount / 100;
            if (currentExp + 1 > maxExp) {
              canAdd = false;
              break;
            }
          }
        }
      }

      if (canAdd) {
        favoritedLineups.push(lineup);
        // Index exposures
        for (const key of lineupKeys) {
          currentPlayerExposure[key] = (currentPlayerExposure[key] || 0) + 1;
        }
        if (lineup.stacks) {
          for (const stack of lineup.stacks) {
            if ((selectedSlate?.sport?.toLowerCase() === 'mlb' && stack.count > 2) ||
                (selectedSlate?.sport?.toLowerCase() === 'nfl' && stack.count > 0)) {
              currentStackExposure[stack.team] = (currentStackExposure[stack.team] || 0) + 1;
            }
          }
        }
      }
    }

    setSavedLineups(favoritedLineups);
  };

  const handleAdjustFavorites = () => {
    // Show snackbar immediately before any state updates
    setSnackbarOpen(true);

    // Commit local changes to parent state (use ref to ensure we have the latest value)
    setPlayerGroups(localGroupsRef.current);

    // Trigger ROI recalculation callback
    onAdjustFavorites();

    // Only try to refavorite if we have saved lineups
    if (savedLineups.length === 0) return;

    const lineupCount = savedLineups.length;

    // Check if the grid has lineup data (not player exposure data)
    let hasLineupData = false;
    try {
      if (apiRef?.current && typeof apiRef.current.getRowModels === 'function') {
        const rowModels = apiRef.current.getRowModels();
        if (rowModels.size > 0) {
          const firstRow = rowModels.values().next().value;
          hasLineupData = !!(firstRow?.exportableLineup && firstRow.exportableLineup.length > 0);
        }
      }
    } catch (e) {
      // Grid not available
    }

    if (hasLineupData) {
      // Grid has lineup data - use grid API for refavoriting
      // Don't clear savedLineups first to avoid visual flash
      setTimeout(() => {
        try {
          apiRef.current.applySorting();

          setTimeout(() => {
            try {
              const newFavoritedLineups = findLineupsToFavorite(
                playerExposureDict,
                stackExposureDict,
                [],
                apiRef,
                selectedSlate,
                lineupCount,
                uniqueLimit ? String(uniqueLimit) : undefined
              );
              setSavedLineups(newFavoritedLineups);
            } catch (e) {
              console.warn('Could not refavorite lineups:', e);
            }
          }, 150);
        } catch (e) {
          console.warn('Could not apply sorting:', e);
        }
      }, 200);
    } else {
      // Grid doesn't have lineup data (e.g., exposure tab) - use lineups array directly
      if (!lineups || lineups.length === 0) return;

      // Use setTimeout to ensure parent state updates are applied before we recalculate
      // Don't clear savedLineups first to avoid visual flash
      setTimeout(() => {
        // Use refs to get the latest values (avoids stale closure issues)
        const currentGroups = localGroupsRef.current;
        const currentPlayerRoiList = playerRoiListRef.current;
        const currentStackRoiList = stackRoiListRef.current;

        // Calculate player ROI boost for a lineup based on individual player boosts
        const getPlayerRoiBoost = (lineup: LineupApiResponse): number => {
          if (!lineup?.exportableLineup) return 0;
          let totalBoost = 0;
          getLineupPlayerKeys(lineup.exportableLineup, selectedSlate).forEach(key => {
            const boost = currentPlayerRoiList[key];
            if (boost !== undefined && boost !== 0) {
              totalBoost += boost / 100; // Convert percentage to decimal
            }
          });
          return totalBoost;
        };

        // Calculate stack ROI boost for a lineup
        const getStackRoiBoost = (lineup: LineupApiResponse): number => {
          if (!lineup?.stacks) return 0;
          let totalBoost = 0;
          lineup.stacks.forEach(stack => {
            if ((selectedSlate?.sport?.toLowerCase() === 'mlb' && stack.count > 2) ||
                (selectedSlate?.sport?.toLowerCase() === 'nfl' && stack.count > 0)) {
              const boost = currentStackRoiList[stack.team];
              if (boost !== undefined && boost !== 0) {
                totalBoost += boost / 100;
              }
            }
          });
          return totalBoost;
        };

        // Apply group boosts, player boosts, and stack boosts, then sort by effective simLineupRoi
        const lineupsWithBoosts = lineups
          .filter(l => l.exportableLineup && l.exportableLineup.length > 0)
          .map(lineup => {
            const groupBoost = getLineupGroupBoost(lineup, currentGroups);
            const playerBoost = getPlayerRoiBoost(lineup);
            const stackBoost = getStackRoiBoost(lineup);
            const effectiveRoi = (lineup.simLineupRoi || 0) + groupBoost + playerBoost + stackBoost;
            return { ...lineup, effectiveRoi };
          })
          .sort((a, b) => b.effectiveRoi - a.effectiveRoi);

        // Helper function to check uniqueness
        const hasCaptain = selectedSlate?.type?.toUpperCase() === 'SHOWDOWN';
        const checkUniqueness = (lineup: LineupApiResponse, existingLineups: string[]): boolean => {
          if (!uniqueLimit || uniqueLimit === 0) return true;

          let players = lineup.exportableLineup.split(',');
          if (hasCaptain) {
            players = players.map((x, i) => i === 0 ? `${x}||CPT` : x);
          }

          return !existingLineups.some(existingLineup => {
            let existingPlayers = existingLineup.split(',');
            if (hasCaptain) {
              existingPlayers = existingPlayers.map((x, i) => i === 0 ? `${x}||CPT` : x);
            }
            const uniquePlayers = players.filter(player => !existingPlayers.includes(player));
            return uniquePlayers.length < uniqueLimit;
          });
        };

        // Select lineups respecting exposure limits and uniqueness
        let currentPlayerExposure: { [key: string]: number } = {};
        let currentStackExposure: { [key: string]: number } = {};
        const globalPlayerExposure = playerExposureDict['global'] || 100;
        const globalStackExposure = stackExposureDict['global'] || 100;
        let favoritedLineups: LineupApiResponse[] = [];
        let favoritedLineupsStrings: string[] = [];

        for (const lineup of lineupsWithBoosts) {
          if (favoritedLineups.length >= lineupCount) break;

          const lineupKeys = getLineupPlayerKeys(lineup.exportableLineup || '', selectedSlate);
          let canAdd = true;

          // Check uniqueness constraint
          if (!checkUniqueness(lineup, favoritedLineupsStrings)) {
            canAdd = false;
          }

          // Check player exposure limits
          if (canAdd) {
            for (const key of lineupKeys) {
              const currentExp = currentPlayerExposure[key] || 0;
              const maxExp = Math.min(playerExposureDict[key] ?? 100, globalPlayerExposure) * lineupCount / 100;
              if (currentExp + 1 > maxExp) {
                canAdd = false;
                break;
              }
            }
          }

          if (canAdd && lineup.stacks) {
            for (const stack of lineup.stacks) {
              if ((selectedSlate?.sport?.toLowerCase() === 'mlb' && stack.count > 2) ||
                  (selectedSlate?.sport?.toLowerCase() === 'nfl' && stack.count > 0)) {
                const currentExp = currentStackExposure[stack.team] || 0;
                const maxExp = Math.min(stackExposureDict[stack.team] ?? 100, globalStackExposure) * lineupCount / 100;
                if (currentExp + 1 > maxExp) {
                  canAdd = false;
                  break;
                }
              }
            }
          }

          if (canAdd) {
            favoritedLineups.push(lineup);
            favoritedLineupsStrings.push(lineup.exportableLineup);
            for (const key of lineupKeys) {
              currentPlayerExposure[key] = (currentPlayerExposure[key] || 0) + 1;
            }
            if (lineup.stacks) {
              for (const stack of lineup.stacks) {
                if ((selectedSlate?.sport?.toLowerCase() === 'mlb' && stack.count > 2) ||
                    (selectedSlate?.sport?.toLowerCase() === 'nfl' && stack.count > 0)) {
                  currentStackExposure[stack.team] = (currentStackExposure[stack.team] || 0) + 1;
                }
              }
            }
          }
        }

        setSavedLineups(favoritedLineups);
      }, 100);
    }
  };

  const handleClearBoosts = () => {
    setLocalGroups(prev => prev.map(group => ({ ...group, roiBoost: 0 })));
  };

  const handleDeleteAllGroups = () => {
    setLocalGroups([]);
    // Also commit to parent state immediately so deletion persists even if modal is closed
    setPlayerGroups([]);
  };

  // Save groups when modal closes (so groups persist even without clicking Adjust Favorites)
  const handleClose = () => {
    setPlayerGroups(localGroupsRef.current);
    closeModal();
  };

  return (
    <>
      <Modal
        open={open}
        disableAutoFocus
        keepMounted
        onClose={(_event: object, reason: string) => {
          if (reason?.toLowerCase() === 'backdropclick') handleClose();
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(125, 125, 125, .65)',
              transition: 'none',
            },
          },
        }}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          color: 'white',
          fontFamily: 'Roboto',
        }}
      >
        <Box sx={{
          backgroundColor: '#0A0A0A',
          borderRadius: 2,
          minHeight: 400,
          width: 900,
          maxHeight: '80vh',
          overflow: 'hidden',
          border: '1px solid rgba(255, 255, 255, .30)',
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '20px 20px 0 20px' }}>
            <Box>
              <Box sx={{ fontSize: 20, fontWeight: 600, marginBottom: 1 }}>Player Groups</Box>
              <Box sx={{ fontSize: 14, fontWeight: 400, color: 'rgba(255, 255, 255, 0.7)', maxWidth: 600, lineHeight: 1.4 }}>
                Create player groups to boost lineups that include all players in the group. The boost only applies when every player in the group appears in the lineup.
              </Box>
            </Box>
            <IconButton onClick={handleClose} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Create New Group Button */}
          <Box sx={{ padding: '16px 20px 0 20px' }}>
            <Button
              variant="outlined"
              startIcon={<AddCircleOutlineIcon />}
              onClick={createNewGroup}
              sx={{
                color: 'white',
                borderColor: 'rgba(1, 90, 206, 0.70)',
                '&:hover': {
                  borderColor: 'rgba(1, 90, 206, 0.90)',
                  backgroundColor: 'rgba(1, 90, 206, 0.1)',
                },
              }}
            >
              CREATE NEW GROUP
            </Button>
          </Box>

          {/* Groups List */}
          <Box sx={{
            flex: 1,
            overflowY: 'auto',
            padding: '16px 20px',
            '&::-webkit-scrollbar': { width: '8px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': { background: 'slategrey', borderRadius: '4px' },
          }}>
            {localGroups.length === 0 ? (
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: 150,
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: 14,
              }}>
                No groups created yet. Click "Create New Group" to get started.
              </Box>
            ) : (
              localGroups.map((group) => (
                <GroupRow
                  key={group.id}
                  group={group}
                  availablePlayers={getAvailablePlayersForGroup(group)}
                  exposure={getGroupExposure(group)}
                  onPlayersChange={updateGroupPlayers}
                  onBoostChange={updateGroupBoost}
                  onDelete={deleteGroup}
                />
              ))
            )}
          </Box>

          {/* Footer */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            backgroundColor: '#0A0A0A'
          }}>
            <Button
              variant="outlined"
              onClick={handleDeleteAllGroups}
              sx={{
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)'
                }
              }}
            >
              DELETE ALL GROUPS
            </Button>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleClearBoosts}
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                  }
                }}
              >
                CLEAR BOOSTS
              </Button>
              <Button
                variant="contained"
                endIcon={<OpenInNewIcon />}
                onClick={handleAdjustFavorites}
                sx={{
                  backgroundColor: '#015ACE',
                  color: 'white',
                  '&:hover': { backgroundColor: '#0148a8' }
                }}
              >
                ADJUST FAVORITES
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          Favorites updated successfully!
        </Alert>
      </Snackbar>
    </>
  );
};

export default PlayerGroupsModal;
